import { toPoint2D } from '@shared/helpers/geometry/geometry.helpers';
import { Point2D } from '@shared/models/geometry.model';
import { Coordinate } from '@shared/models/geography.model';
import mapboxgl, { MapMouseEvent } from 'mapbox-gl';
import { BehaviorSubject, Subscription } from 'rxjs';
import { MapLayerIds } from '../helpers/map-config.constants';
import { getMetersPerPixelAtCoordinate } from '../helpers/map.helpers';
import { shouldShowLocationPin } from '../helpers/location-pin-visibility.helpers';
import { MapObservables } from '../helpers/map-observables.helpers';
import { MapStateObservables } from '../models';

export type BuildingTranslationHandler =
  ((location: Coordinate) => Promise<void> | void) | undefined;

export type LocationPinCursorState = {
  active: boolean;
  hover: boolean;
  dragging: boolean;
  canTranslate: boolean;
};

type LocationPinControllerParams = {
  state: MapStateObservables;
  resolveMap: () => mapboxgl.Map | undefined;
  resolveObservables: () => MapObservables | undefined;
  getCurrentLocation: () => Coordinate;
  isDesignEmpty: () => boolean;
  onTooltipUpdate: () => void;
};

export class MapLocationPinController {
  private readonly state: MapStateObservables;

  private readonly resolveMap: () => mapboxgl.Map | undefined;

  private readonly resolveObservables: () => MapObservables | undefined;

  private readonly getCurrentLocation: () => Coordinate;

  private readonly isDesignEmpty: () => boolean;

  private readonly onTooltipUpdate: () => void;

  private pinModeSubscription = new Subscription();

  private interactionSubscription = new Subscription();

  private locationPinModeActive = false;

  private isLocationPinHovering = false;

  private locationPinHoverPoint: Point2D | undefined;

  private isLocationPinDragging = false;

  private isReadonly = false;

  private spacePanActive = false;

  private pendingLocationPin: Coordinate | undefined;

  private locationPinDragOrigin: Coordinate | undefined;

  /** Offset from cursor to pin at mousedown (lng/lat delta) so drag preserves click position. */
  private locationPinDragOffset: { lng: number; lat: number } | undefined;

  private committedLocation: Coordinate;

  /** Identifies the newest mouse-up when multiple pin saves overlap. */
  private locationCommitSequence = 0;

  private buildingTranslationHandler: BuildingTranslationHandler;

  private readonly cursorState$ = new BehaviorSubject<LocationPinCursorState>({
    active: false,
    hover: false,
    dragging: false,
    canTranslate: false,
  });

  constructor({
    state,
    resolveMap,
    resolveObservables,
    getCurrentLocation,
    isDesignEmpty,
    onTooltipUpdate,
  }: LocationPinControllerParams) {
    this.state = state;
    this.resolveMap = resolveMap;
    this.resolveObservables = resolveObservables;
    this.getCurrentLocation = getCurrentLocation;
    this.isDesignEmpty = isDesignEmpty;
    this.onTooltipUpdate = onTooltipUpdate;
    this.committedLocation = { ...state.location$.getValue() };
    this.buildingTranslationHandler = undefined;
  }

  /**
   * Ensure controller reacts to a newly attached map.
   */
  attach(): void {
    this.setupPinModeTracking();
    this.setupLocationPinInteraction();
    this.updateLocationPinCursor();
  }

  /**
   * Clean up subscriptions tied to the map instance.
   */
  detach(): void {
    this.pinModeSubscription.unsubscribe();
    this.pinModeSubscription = new Subscription();
    this.interactionSubscription.unsubscribe();
    this.interactionSubscription = new Subscription();
    this.cancelLocationPinDrag();
    this.isLocationPinHovering = false;
    this.setLocationPinHoverPoint(undefined);
    this.updateLocationPinCursor();
  }

  /**
   * Keep internal state in sync with external location updates.
   * @param location {@link Coordinate}
   */
  handleLocationChange(location: Coordinate): void {
    if (!this.isLocationPinDragging) {
      this.committedLocation = { ...location };
    }
    this.evaluateLocationPinMode();
  }

  /**
   * React to external pin mode toggles.
   * @param isPinMode boolean
   */
  handlePinModeStateChange(isPinMode: boolean): void {
    this.locationPinModeActive = isPinMode;
    if (!isPinMode) {
      this.cancelLocationPinDrag();
      this.isLocationPinHovering = false;
      this.setLocationPinHoverPoint(undefined);
    } else {
      this.isLocationPinHovering = false;
      this.setLocationPinHoverPoint(undefined);
    }
    this.updateLocationPinCursor();
    this.onTooltipUpdate();
  }

  /**
   * Register (or clear) building translation handler.
   * @param handler building translation callback
   */
  setBuildingTranslationHandler(handler: BuildingTranslationHandler): void {
    this.buildingTranslationHandler = handler;
    if (!handler) {
      this.cancelLocationPinDrag();
      this.setLocationPinHoverPoint(undefined);
    }
    this.updateLocationPinCursor();
    this.onTooltipUpdate();
  }

  /**
   * Toggle read-only mode for location pin interactions.
   * @param isReadonly flag to disable pin interactions
   */
  setReadonly(isReadonly: boolean): void {
    if (this.isReadonly === isReadonly) return;
    this.isReadonly = isReadonly;
    if (isReadonly) {
      // Ensure any in-flight drag is cancelled cleanly.
      this.cancelLocationPinDrag();
      this.isLocationPinHovering = false;
      this.setLocationPinHoverPoint(undefined);
      if (this.state.locationPinMode$.getValue()) {
        this.state.locationPinMode$.next(false);
      }
    } else {
      this.evaluateLocationPinMode();
    }
    this.updateLocationPinCursor();
  }

  /** Let Space-pan take precedence over hover and drag on the location pin. */
  setSpacePanActive(active: boolean): void {
    if (this.spacePanActive === active) return;
    this.spacePanActive = active;
    if (active) {
      this.cancelLocationPinDrag();
      this.clearLocationPinHover();
    }
  }

  getCursorState$(): BehaviorSubject<LocationPinCursorState> {
    return this.cursorState$;
  }

  /**
   * Determine if pin mode should be active given current view.
   */
  evaluateLocationPinMode(): void {
    const mapInstance = this.resolveMap();
    if (!mapInstance) return;
    const location = this.getCurrentLocation();
    const metersPerPixel = getMetersPerPixelAtCoordinate(mapInstance, location);
    const shouldShowPin = shouldShowLocationPin({
      isReadonly: this.isReadonly,
      isDesignEmpty: this.isDesignEmpty(),
      metersPerPixel,
    });
    if (this.state.locationPinMode$.getValue() === shouldShowPin) return;
    this.state.locationPinMode$.next(shouldShowPin);
  }

  /**
   * Expose whether pin mode is currently active.
   */
  isPinModeActive(): boolean {
    return this.locationPinModeActive;
  }

  /**
   * Expose hover point for tooltip logic.
   */
  getHoverPoint(): Point2D | undefined {
    return this.locationPinHoverPoint;
  }

  /**
   * Reveal whether building movement is enabled.
   */
  canTranslateBuilding(): boolean {
    return Boolean(this.buildingTranslationHandler);
  }

  /**
   * Cancel ongoing drag, restoring previous location.
   */
  cancelLocationPinDrag(): void {
    if (!this.isLocationPinDragging) return;
    const restore = this.locationPinDragOrigin ?? { ...this.committedLocation };
    this.state.location$.next(restore);
    this.locationPinDragOrigin = undefined;
    this.locationPinDragOffset = undefined;
    this.pendingLocationPin = undefined;
    this.isLocationPinDragging = false;
    this.resolveMap()?.dragPan?.enable?.();
    this.updateLocationPinCursor();
  }

  private setupPinModeTracking(): void {
    const observables = this.resolveObservables();
    const mapInstance = this.resolveMap();
    if (!observables || !mapInstance) return;
    this.pinModeSubscription.unsubscribe();
    this.pinModeSubscription = new Subscription();
    ['zoom', 'pitch', 'resize'].forEach((eventName) => {
      this.pinModeSubscription.add(
        observables.map(eventName as any).subscribe(() => {
          this.evaluateLocationPinMode();
        }),
      );
    });
    this.evaluateLocationPinMode();
  }

  private setupLocationPinInteraction(): void {
    const observables = this.resolveObservables();
    if (!observables || !this.resolveMap()) return;
    this.interactionSubscription.unsubscribe();
    this.interactionSubscription = new Subscription();

    const mapMouseMove$ = observables.map('mousemove');
    const mapMouseDown$ = observables.map('mousedown');
    const mapMouseUp$ = observables.map('mouseup');
    const domMouseUp$ = observables.dom.mouse.up$;

    this.interactionSubscription.add(
      mapMouseMove$.subscribe((event) => {
        this.handleLocationPinMouseMove(event as MapMouseEvent);
      }),
    );

    this.interactionSubscription.add(
      mapMouseDown$.subscribe((event) => {
        this.handleLocationPinMouseDown(event as MapMouseEvent);
      }),
    );

    this.interactionSubscription.add(
      mapMouseUp$.subscribe((event) => {
        void this.handleLocationPinMouseUp(event as MapMouseEvent);
      }),
    );

    this.interactionSubscription.add(
      domMouseUp$.subscribe(() => {
        void this.handleLocationPinMouseUp();
      }),
    );
  }

  private handleLocationPinMouseMove(event: MapMouseEvent): void {
    if (this.spacePanActive) {
      this.clearLocationPinHover();
      return;
    }
    if (this.isReadonly) {
      this.clearLocationPinHover();
      return;
    }
    if (!this.locationPinModeActive) {
      this.clearLocationPinHover();
      return;
    }
    if (this.isLocationPinDragging && this.locationPinDragOffset) {
      this.setLocationPinHoverPoint(this.getScreenPointFromEvent(event));
      this.updateLocationPinFromLngLatWithOffset(event.lngLat);
      return;
    }
    const isHovering = this.isLocationPinEvent(event);
    if (!isHovering) {
      this.clearLocationPinHover();
      return;
    }
    if (!this.isLocationPinHovering) {
      this.isLocationPinHovering = true;
      this.updateLocationPinCursor();
    }
    this.setLocationPinHoverPoint(this.getScreenPointFromEvent(event));
  }

  private clearLocationPinHover(): void {
    const hadHover =
      this.isLocationPinHovering || Boolean(this.locationPinHoverPoint);
    if (!hadHover) return;
    this.isLocationPinHovering = false;
    this.setLocationPinHoverPoint(undefined);
    this.updateLocationPinCursor();
  }

  private handleLocationPinMouseDown(event: MapMouseEvent): void {
    if (this.spacePanActive) return;
    if (this.isReadonly) return;
    if (!this.locationPinModeActive) return;
    if (!this.buildingTranslationHandler) return;
    if (!this.isLocationPinEvent(event)) return;
    event.preventDefault();
    const location = this.getCurrentLocation();
    if (!this.locationPinDragOrigin) {
      this.locationPinDragOrigin = {
        lng: location.lng,
        lat: location.lat,
      };
    }
    // Preserve cursor-to-pin offset so the building does not jump on mousedown.
    this.locationPinDragOffset = {
      lng: event.lngLat.lng - location.lng,
      lat: event.lngLat.lat - location.lat,
    };
    this.isLocationPinDragging = true;
    this.resolveMap()?.dragPan?.disable?.();
    this.isLocationPinHovering = false;
    this.setLocationPinHoverPoint(undefined);
    this.updateLocationPinCursor();
  }

  private async handleLocationPinMouseUp(event?: MapMouseEvent): Promise<void> {
    if (this.isReadonly) {
      this.cancelLocationPinDrag();
      return;
    }
    if (!this.isLocationPinDragging) return;
    if (event && this.locationPinDragOffset) {
      this.updateLocationPinFromLngLatWithOffset(event.lngLat);
    }
    this.locationPinDragOffset = undefined;
    this.isLocationPinDragging = false;
    this.resolveMap()?.dragPan?.enable?.();
    const locationToCommit =
      this.pendingLocationPin ?? this.getCurrentLocation();
    const locationToRestore = this.locationPinDragOrigin ?? {
      ...this.committedLocation,
    };
    const commitSequence = ++this.locationCommitSequence;
    this.pendingLocationPin = undefined;
    this.locationPinDragOrigin = undefined;
    const didCommit = await this.commitBuildingTranslation(locationToCommit);
    if (commitSequence !== this.locationCommitSequence) return;
    if (didCommit) {
      // Persist can recenter the pin onto the footprint midpoint.
      this.committedLocation = { ...this.getCurrentLocation() };
    } else {
      this.committedLocation = { ...locationToRestore };
      this.state.location$.next(locationToRestore);
    }
    this.updateLocationPinCursor();
  }

  /**
   * Update pin location from cursor lng/lat, applying the stored drag offset
   * so the pin stays under the same point of the cursor as at mousedown.
   */
  private updateLocationPinFromLngLatWithOffset(lngLat: mapboxgl.LngLat): void {
    const offset = this.locationPinDragOffset;
    if (!offset) return;
    const nextLocation: Coordinate = {
      lng: lngLat.lng - offset.lng,
      lat: lngLat.lat - offset.lat,
    };
    this.pendingLocationPin = nextLocation;
    this.state.location$.next(nextLocation);
  }

  private isLocationPinEvent(event: MapMouseEvent): boolean {
    const mapInstance = this.resolveMap();
    if (!mapInstance) return false;
    if (!this.locationPinModeActive) return false;
    if (!mapInstance.getLayer(MapLayerIds.LocationPin)) return false;
    const features = mapInstance.queryRenderedFeatures(event.point, {
      layers: [MapLayerIds.LocationPin],
    });
    return Boolean(features?.length);
  }

  private updateLocationPinCursor(): void {
    const next: LocationPinCursorState = {
      active: this.locationPinModeActive,
      hover: this.isLocationPinHovering,
      dragging: this.isLocationPinDragging,
      canTranslate:
        this.locationPinModeActive &&
        this.isLocationPinHovering &&
        Boolean(this.buildingTranslationHandler),
    };
    const current = this.cursorState$.getValue();
    if (
      current.active === next.active &&
      current.hover === next.hover &&
      current.dragging === next.dragging &&
      current.canTranslate === next.canTranslate
    ) {
      return;
    }
    this.cursorState$.next(next);
  }

  private setLocationPinHoverPoint(point: Point2D | undefined): void {
    const previous = this.locationPinHoverPoint;
    if (
      previous &&
      point &&
      previous[0] === point[0] &&
      previous[1] === point[1]
    ) {
      return;
    }
    if (!previous && !point) return;
    this.locationPinHoverPoint = point;
    this.onTooltipUpdate();
  }

  private getScreenPointFromEvent(event: MapMouseEvent): Point2D | undefined {
    const mapInstance = this.resolveMap();
    const referencePoint = event.point ?? mapInstance?.project(event.lngLat);
    if (!referencePoint) return undefined;
    return toPoint2D(referencePoint);
  }

  private async commitBuildingTranslation(
    location: Coordinate,
  ): Promise<boolean> {
    if (!this.buildingTranslationHandler) return false;
    try {
      await this.buildingTranslationHandler(location);
      return true;
    } catch (err) {
      console.warn('Failed to update building location', err);
      return false;
    }
  }
}
